import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Save as SaveIcon,
  Create as SignIcon,
  ArrowBack as BackIcon,
  Clear as ClearIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  HealthAndSafety as HealthIcon,
  Person as PersonIcon,
  Coronavirus as MedicalIcon
} from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { toast } from "react-toastify";
import SignatureCanvas from "react-signature-canvas";
import { MEDICAL_BILLING_PERSON, secureApi } from "../config";
import { Context } from "../context";
import TopNav from "../components/topnav";

const useStyles = makeStyles((theme) => ({
  formContainer: {
    paddingTop: 20,
    paddingBottom: 40,
  },
  pageHeader: {
    marginBottom: 20,
    textAlign: "center",
  },
  formTitle: {
    marginBottom: 20,
    textAlign: "center",
    fontWeight: "500",
  },
  bold: {
    fontWeight: "bold",
  },
  underline: {
    textDecoration: "underline",
  },
  signatureBox: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: 10,
    minHeight: 60,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  signatureImg: {
    maxHeight: "50px",
    maxWidth: "100%",
  },
  actionButtons: {
    marginTop: 30,
  },
  headerLogo: {
    backgroundColor: "#f5f5f5",
    padding: 16,
    marginBottom: 24,
    borderRadius: 4,
  },
  bodyText: {
    fontSize: 16,
    lineHeight: 1.6,
    marginBottom: 16,
  },
  paragraph: {
    marginBottom: 16,
  }
}));

const today = new Date();
const year = today.getFullYear();
const month = String(today.getMonth() + 1).padStart(2, "0");
const date = String(today.getDate()).padStart(2, "0");
const formattedDate = `${month}/${date}/${year}`;

const PregnancyWaiverForm = () => {
  const classes = useStyles();
  const { patient_id } = useParams();
  const navigate = useNavigate();
  const { setLoggedIn, user } = useContext(Context);

  useEffect(() => {
    if (user && user.role === MEDICAL_BILLING_PERSON) {
      toast.warn("You don't have permission to access this page.");
      navigate("/");
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState({});
  const [patientSignature, setPatientSignature] = useState("");
  const [patientSignatureDate, setPatientSignatureDate] = useState(formattedDate);
  
  // Signature pad
  const [signPad, setSignPad] = useState({});
  const [signed, setSigned] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);

  const handleClickPatientSign = () => {
    setSignModalOpen(true);
  };
  
  const handleClear = () => {
    signPad.clear();
    setSigned(false);
  };

  const handleSignModalClose = (event, reason) => {
    if (reason !== "backdropClick") {
      handleClear();
      setSignModalOpen(false);
    }
  };

  const handleSign = () => {
    const physicalSign = signPad.getTrimmedCanvas().toDataURL("image/png");
    setPatientSignature(physicalSign);
    if (patientSignatureDate.trim() === "")
      setPatientSignatureDate(formattedDate);
    handleSignModalClose();
  };

  const handleSave = () => {
    setLoading(true);
    
    const formData = {
      patientName:
        patient &&
        patient.patientInfo &&
        `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`,
      patientSignature,
      patientSignatureDate,
    };
    
    secureApi(localStorage.getItem("alex-med-token"))
      .post(`/pregnancy-waiver-form/${patient_id}`, formData)
      .then(() => {
        toast.success("Pregnancy Waiver form has been saved successfully!");
        navigate(`/patient/${patient_id}`);
      })
      .catch((e) => {
        console.log(e);
        if (e.response && e.response.status === 401) {
          toast.warn("Session has been expired. You need to login again!");
          setLoggedIn(false);
          navigate("/login");
        } else if (e.response) {
          toast.error(e.response.data.message);
        } else {
          toast.error("An error occurred while saving the form.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (patient_id) {
      setLoading(true);
      secureApi(localStorage.getItem("alex-med-token"))
        .get(`patient/${patient_id}`)
        .then((response) => {
          const { data: patient } = response;
          setPatient(patient);
          if (patient && patient.pregnancyWaiverForm) {
            setPatientSignature(patient.pregnancyWaiverForm.patientSignature || "");
            setPatientSignatureDate(patient.pregnancyWaiverForm.patientSignatureDate || formattedDate);
          }
        })
        .catch((e) => {
          console.log(e);
          if (e.response && e.response.status === 401) {
            toast.warn("Session has been expired. You need to login again!");
            setLoggedIn(false);
            navigate("/login");
          } else if (e.response) {
            toast.error(e.response.data.message);
          } else {
            toast.error("An error occurred while loading patient data.");
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      toast.warn("Invalid patient ID!");
      navigate("/");
    }
  }, [patient_id, navigate, setLoggedIn]);

  const getPatientName = () => {
    return patient && patient.patientInfo
      ? `${patient.patientInfo.first_name} ${patient.patientInfo.last_name}`
      : "";
  };

  return (
    <>
      <TopNav />
      {loading && <LinearProgress />}
      <Container maxWidth="md" className={classes.formContainer}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header with medical practice info */}
          <Card className={classes.headerLogo} elevation={0}>
            <CardContent>
              <Typography variant="h5" align="center" fontWeight="bold" color="text.secondary">
                Modal Medical Services
              </Typography>
              
              <Typography variant="subtitle1" align="center" color="text.secondary">
                369 LEXINGTON AVE, NEW YORK NE 10017 | NPI: 1316662604 | TIN: 88-2458377
              </Typography>
              
              <Box display="flex" justifyContent="center" alignItems="center" mt={1}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                  Dr. Alexander Movshis
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 1 }}>
                  | NPI: 1942741160
                </Typography>
              </Box>
            </CardContent>
          </Card>
          
          {/* Form Title */}
          <Box mb={4}>
            <Typography variant="h4" className={classes.formTitle} color="primary">
              Pregnancy Waiver Form for Pain Management Procedures
            </Typography>
            
            <Typography variant="h6" align="center" sx={{ mt: 1 }}>
              Patient Acknowledgment and Waiver of Pregnancy Test
            </Typography>
          </Box>
          
          {/* Warning Alert */}
          <Alert 
            severity="warning" 
            icon={<WarningIcon />} 
            sx={{ mb: 4 }}
          >
            <Typography variant="body1" fontWeight="medium">
              This form documents your choice to waive a pregnancy test before undergoing pain management procedures.
              Please read the information carefully before signing.
            </Typography>
          </Alert>
          
          {/* Main Content */}
          <Card elevation={2} sx={{ mb: 4 }}>
            <CardContent>
              <Box display="flex" gap={2} mb={2}>
                <MedicalIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography className={classes.bodyText}>
                  I, the undersigned, acknowledge that I have been informed of the
                  potential risks and implications of undergoing pain management
                  procedures while pregnant. I understand that certain medications,
                  injections, procedures, and imaging studies used in these treatments
                  may pose risks to an unborn child.
                </Typography>
              </Box>
              
              <Box display="flex" gap={2} mb={2}>
                <HealthIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography className={classes.bodyText}>
                  I have also been informed of the option to take a pregnancy test prior
                  to the administration of any procedure to confirm that I am not
                  pregnant. I acknowledge that the pregnancy test is offered to ensure
                  the safety of both myself and any potential pregnancy.
                </Typography>
              </Box>
              
              <Box display="flex" gap={2}>
                <CheckIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography className={classes.bodyText}>
                  By signing this waiver, I am voluntarily choosing to decline the
                  pregnancy test and affirm that, to the best of my knowledge, I am not
                  pregnant at this time. I understand and accept full responsibility for
                  any risks or complications that may arise as a result of declining
                  this test.
                </Typography>
              </Box>
            </CardContent>
          </Card>
          
          {/* Patient Consent */}
          <Box mb={4}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Patient Consent
            </Typography>
            
            <Typography className={classes.bodyText}>
              I hereby release the practice and its staff from any responsibility or
              liability related to any adverse effects that may occur due to the
              lack of pregnancy confirmation. I acknowledge that I have had the
              opportunity to ask questions regarding the risks of undergoing pain
              management procedures while potentially pregnant, and I understand the
              information provided to me.
            </Typography>
          </Box>
          
          {/* Signature Section */}
          <Card elevation={3} sx={{ p: 2, mb: 4 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Patient Name
                  </Typography>
                  <TextField
                    value={getPatientName()}
                    variant="outlined"
                    fullWidth
                    size="small"
                    InputProps={{
                      readOnly: true,
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Date
                  </Typography>
                  <TextField
                    value={patientSignatureDate}
                    onChange={(e) => setPatientSignatureDate(e.target.value)}
                    variant="outlined"
                    fullWidth
                    size="small"
                    placeholder="MM/DD/YYYY"
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
                    Signature
                  </Typography>
                  <Box 
                    className={classes.signatureBox}
                    onClick={handleClickPatientSign}
                    sx={{ cursor: 'pointer' }}
                  >
                    {patientSignature ? (
                      <img
                        src={patientSignature}
                        alt="Patient signature"
                        className={classes.signatureImg}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click here to sign
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          
          {/* Action Buttons */}
          <Box display="flex" justifyContent="space-between" className={classes.actionButtons}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(-1)}
              startIcon={<BackIcon />}
            >
              Back to Patient
            </Button>
            
            <Box>
              <Button
                variant="outlined"
                color="error"
                onClick={() => navigate(-1)}
                sx={{ mr: 2 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                endIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading || !patientSignature}
              >
                Save Form
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
      
      {/* Signature Dialog */}
      <Dialog 
        open={signModalOpen} 
        onClose={handleSignModalClose} 
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <PersonIcon sx={{ mr: 1 }} />
            Patient Signature
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in the space below to confirm you have read and understand the pregnancy waiver:
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "5px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <SignatureCanvas
                penColor="black"
                canvasProps={{ 
                  width: 600, 
                  height: 200, 
                  className: "sigCanvas" 
                }}
                ref={(ref) => {
                  setSignPad(ref);
                }}
                onEnd={() => setSigned(true)}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button 
            onClick={handleClear} 
            variant="outlined" 
            color="warning"
            startIcon={<ClearIcon />}
          >
            Clear
          </Button>
          <Button 
            onClick={handleSign} 
            variant="contained" 
            disabled={!signed}
            endIcon={<SignIcon />}
            color="primary"
          >
            Confirm Signature
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PregnancyWaiverForm;